/**
 * NotificationBell Component
 * Bell icon with dropdown showing recent notifications
 */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../shared/hooks/useNotifications';
import { NotificationItem } from './NotificationItem';
import { Notification } from '../../types/notification.types';
import Svg from '../SVG/svg';
import './NotificationBell.css';

export const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refresh
  } = useNotifications({ limit: 10 });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleNotificationClick = (notification: Notification) => {
    setIsOpen(false);
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleViewAll = () => {
    setIsOpen(false);
    navigate('/notifications');
  };

  return (
    <div
      className='notification-bell-container'
      ref={dropdownRef}
    >
      <button
        className='notification-bell-button'
        onClick={handleToggle}
        aria-label='Notifications'
      >
        <Svg type='bell' />
        {unreadCount > 0 && (
          <span className='notification-bell-badge'>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className='notification-bell-dropdown'>
          <div className='notification-bell-header'>
            <h3 className='notification-bell-title'>Notifications</h3>
            <div className='notification-bell-header-actions'>
              {unreadCount > 0 && (
                <button
                  className='notification-bell-mark-read'
                  onClick={handleMarkAllAsRead}
                >
                  Mark all as read
                </button>
              )}
              <button
                className='notification-bell-close'
                onClick={() => setIsOpen(false)}
                aria-label='Close notifications'
              >
                &times;
              </button>
            </div>
          </div>

          <div className='notification-bell-list'>
            {loading && (!notifications || notifications.length === 0) ? (
              <div className='notification-bell-empty'>
                <div
                  className='spinner-border spinner-border-sm text-secondary'
                  role='status'
                >
                  <span className='visually-hidden'>Loading...</span>
                </div>
              </div>
            ) : !notifications || notifications.length === 0 ? (
              <div className='notification-bell-empty'>
                <Svg type='bell' />
                <p>No notifications yet</p>
              </div>
            ) : (
              <ul style={{ padding: 0, margin: 0 }}>
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification._id}
                    notification={notification}
                    onMarkAsRead={markAsRead}
                    onDelete={deleteNotification}
                    onClick={handleNotificationClick}
                  />
                ))}
              </ul>
            )}
          </div>

          {notifications.length > 0 && (
            <div className='notification-bell-footer'>
              <button
                className='notification-bell-view-all'
                onClick={handleViewAll}
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
